import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, 
  ArrowLeft,
  ExternalLink,
  CheckCircle2,
  Users,
  Mail,
  FileText,
  Clock,
  AlertCircle,
  Workflow,
  LayoutDashboard,
  Bell,
  Settings,
  Shield,
  Database,
  Code,
  Zap,
  Calendar,
  MessageSquare,
  Search,
  Lightbulb,
  Rocket,
  Target,
  X,
  ChevronLeft,
  ChevronRight,
  Star,
  Quote,
  UserCircle,
  Play,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const METRICS_CACHE_KEY = 'mushonga_portfolio_metrics_repair_cafe_v2';

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={countRef}>
      {typeof end === 'number' ? count : end}{suffix}
    </span>
  );
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  if (currentIndex === null) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 transition-colors"
      >
        <X size={32} />
      </button>
      
      <button 
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 text-white/80 hover:text-white p-2 transition-colors"
      >
        <ChevronLeft size={40} />
      </button>
      
      <div className="max-w-5xl max-h-[80vh] relative" onClick={(e) => e.stopPropagation()}>
        <img 
          src={images[currentIndex].url} 
          alt={images[currentIndex].caption}
          className="max-w-full max-h-[75vh] object-contain rounded-lg"
        />
        <p className="text-center text-white mt-4 text-lg">{images[currentIndex].caption}</p>
      </div>
      
      <button 
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 text-white/80 hover:text-white p-2 transition-colors"
      >
        <ChevronRight size={40} />
      </button>
    </div>
  );
};

const CaseStudyPage = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  
  // Live metrics state
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // Fetch live metrics on mount
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setMetricsLoading(true);
        const response = await axios.get(`${API_URL}/api/public/portfolio-metrics/repair-cafe`);
        
        if (response.data.success && response.data.metrics) {
          setLiveMetrics(response.data.metrics);
          setLastSyncedAt(response.data.last_synced_at);
          setMetricsError(false);
          
          // Cache the successful response with timestamp
          localStorage.setItem(METRICS_CACHE_KEY, JSON.stringify({
            metrics: response.data.metrics,
            last_synced_at: response.data.last_synced_at,
            cached_at: new Date().toISOString()
          }));
        }
      } catch (error) {
        console.error('Failed to fetch live metrics:', error);
        setMetricsError(true);
        
        // Try to load from cache on error
        const cached = localStorage.getItem(METRICS_CACHE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.metrics) {
              setLiveMetrics(parsed.metrics);
              setLastSyncedAt(parsed.last_synced_at);
            }
          } catch (e) {
            console.error('Failed to parse cached metrics:', e);
          }
        }
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  // Build metrics array for display
  // Note: completed_requests is still fetched and stored in liveMetrics but not displayed
  // It will be used when a lifetime cumulative completed-repairs metric becomes available
  const getProjectMetrics = () => {
    if (liveMetrics) {
      return [
        { value: liveMetrics.total_requests, suffix: '', label: 'Repair Requests Submitted' },
        { value: liveMetrics.assigned_requests, suffix: '', label: 'Assigned Requests' },
        { value: liveMetrics.awaiting_assignment, suffix: '', label: 'Awaiting Assignment' },
        { value: 'Live', suffix: '', label: 'Production Deployment' }
      ];
    }
    // Fallback if no live data available (should rarely happen)
    return [
      { value: null, suffix: '', label: 'Repair Requests Submitted' },
      { value: null, suffix: '', label: 'Assigned Requests' },
      { value: null, suffix: '', label: 'Awaiting Assignment' },
      { value: 'Live', suffix: '', label: 'Production Deployment' }
    ];
  };

  const galleryImages = [
    { url: 'https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/806hwo39_Repair%20Cafe%20Homepage.png', caption: 'Homepage' },
    { url: 'https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/wotbl5r8_Online%20Repair%20Request%20Portal.png', caption: 'Repair Request Form' },
    { url: 'https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/blidx0q1_Admin%20dashboard.png', caption: 'Management Dashboard' },
    { url: 'https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/1p4idiuv_n8n%20workflow.png', caption: 'Automation Workflow' }
  ];

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  const prevImage = () => setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="case-study-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="section-container">
          <nav className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/mushonga_logo_circular.png"
                alt="Mushonga Systems"
                className="h-10 w-auto"
              />
              <span className="text-white font-bold text-lg hidden sm:block">
                Mushonga Systems
              </span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </nav>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-emerald-500/10 rounded-full blur-[120px]"></div>
          </div>

          <div className="section-container relative z-10">
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                LIVE PRODUCTION SYSTEM
              </span>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left - Logo */}
              <div className="flex justify-center lg:justify-start">
                <div className="p-8 sm:p-12">
                  <img
                    src="https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/0meyz3iq_Logo.png"
                    alt="Peterborough Repair Café"
                    className="w-full max-w-xs sm:max-w-sm h-auto"
                  />
                </div>
              </div>

              {/* Right - Content */}
              <div>
                <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-2">
                  Community Charity Digital Transformation
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white mb-4">
                  Peterborough Repair Café UK
                </h1>
                <p className="text-gray-500 text-sm mb-6">Community Repair Charity</p>

                <p className="text-gray-400 leading-relaxed mb-8 text-base sm:text-lg">
                  We transformed a manual repair request process into a fully digital workflow, giving volunteers, administrators and customers a single system to manage repair requests from submission through to completion.
                </p>

                <a
                  href="https://peterboroughrepaircafe.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex"
                  data-testid="view-live-project"
                >
                  View Live Project
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* The Challenge Section */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Background
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                The Challenge
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-[#1F2937] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <AlertCircle className="text-red-400" size={20} />
                  </div>
                  Previous Process
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Before the system was built, repair requests were managed through:
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: <Mail size={16} />, text: 'Email communication' },
                    { icon: <FileText size={16} />, text: 'Manual administration' },
                    { icon: <Users size={16} />, text: 'Volunteer coordination' },
                    { icon: <Database size={16} />, text: 'Spreadsheet tracking' },
                    { icon: <Settings size={16} />, text: 'Multiple disconnected processes' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                      <span className="text-gray-500">{item.icon}</span>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#1F2937] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="text-amber-400" size={20} />
                  </div>
                  Pain Points
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  This created significant operational issues:
                </p>
                <ul className="space-y-3">
                  {[
                    'Administrative overhead',
                    'Difficult request tracking',
                    'Communication delays',
                    'Volunteer assignment inefficiencies',
                    'Increased risk of missed requests'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                      <X className="text-red-400 flex-shrink-0" size={14} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* The Solution Section */}
        <section className="py-16 sm:py-24">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                What We Built
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                The Solution
              </h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                We designed and deployed a complete repair management platform including:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {[
                { icon: <Globe size={20} />, text: 'Public-facing website' },
                { icon: <FileText size={20} />, text: 'Online repair request form' },
                { icon: <LayoutDashboard size={20} />, text: 'Administrative dashboard' },
                { icon: <Users size={20} />, text: 'Volunteer assignment system' },
                { icon: <Bell size={20} />, text: 'Automated notifications' },
                { icon: <Workflow size={20} />, text: 'Workflow automation' },
                { icon: <Shield size={20} />, text: 'User management' },
                { icon: <Search size={20} />, text: 'Reporting and tracking' }
              ].map((item, i) => (
                <div 
                  key={i}
                  className="bg-[#111827] border border-white/10 rounded-xl p-4 sm:p-5 flex flex-col items-center text-center card-glow"
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-3">
                    <div className="text-emerald-400">{item.icon}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={14} />
                    <span className="text-gray-300 text-xs sm:text-sm">{item.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Metrics Section */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Real Numbers
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Project Metrics
              </h2>
              {/* Live data indicator */}
              {liveMetrics && !metricsLoading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <span>Live operational data</span>
                  {lastSyncedAt && (
                    <span className="text-gray-600">
                      · Updated {new Date(lastSyncedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {getProjectMetrics().map((metric, i) => (
                <div 
                  key={i}
                  className="bg-[#1F2937] border border-white/10 rounded-2xl p-6 text-center"
                >
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-2">
                    {metricsLoading && metric.value === null ? (
                      <span className="inline-block w-16 h-10 bg-gray-700 rounded animate-pulse"></span>
                    ) : typeof metric.value === 'number' ? (
                      <AnimatedCounter end={metric.value} suffix={metric.suffix} />
                    ) : metric.value === null ? (
                      <span className="text-gray-600">—</span>
                    ) : (
                      <span className="text-emerald-400">{metric.value}</span>
                    )}
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm">{metric.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {['Customer', 'Volunteer', 'Admin', 'Super Admin'].map((role, i) => (
                <span 
                  key={i}
                  className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium px-4 py-2 rounded-full"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshot Gallery */}
        <section className="py-16 sm:py-24">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Visual Preview
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                System Screenshots
              </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {galleryImages.map((image, i) => (
                <div 
                  key={i}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(i)}
                >
                  <div className="relative rounded-xl overflow-hidden border border-white/10 aspect-video bg-[#111827]">
                    <img 
                      src={image.url} 
                      alt={image.caption}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <Search className="text-white" size={24} />
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm text-center mt-3">{image.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* System Architecture */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                System Flow
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                How The System Works
              </h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-500 to-emerald-500/20 transform -translate-x-1/2 hidden md:block"></div>

                {[
                  { icon: <UserCircle size={24} />, title: 'Customer', desc: 'Submits repair request through website' },
                  { icon: <FileText size={24} />, title: 'Repair Request Submitted', desc: 'Form data captured and stored' },
                  { icon: <LayoutDashboard size={24} />, title: 'Management Dashboard', desc: 'Admin reviews incoming requests' },
                  { icon: <Users size={24} />, title: 'Volunteer Assignment', desc: 'Request assigned to skilled volunteer' },
                  { icon: <Bell size={24} />, title: 'Automated Notifications', desc: 'All parties notified automatically' },
                  { icon: <Settings size={24} />, title: 'Repair Progress Tracking', desc: 'Status updates throughout process' },
                  { icon: <MessageSquare size={24} />, title: 'Customer Follow-Up', desc: 'Final notification on completion' }
                ].map((step, i) => (
                  <div 
                    key={i}
                    className={`relative flex items-center gap-4 sm:gap-8 mb-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`bg-[#1F2937] border border-white/10 rounded-xl p-4 sm:p-5 inline-block ${i % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                        <h4 className="text-white font-semibold mb-1">{step.title}</h4>
                        <p className="text-gray-500 text-sm">{step.desc}</p>
                      </div>
                    </div>
                    
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-500/20 border-2 border-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 z-10">
                      <div className="text-emerald-400">{step.icon}</div>
                    </div>
                    
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technology Stack */}
        <section className="py-16 sm:py-24">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Built With
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Technology Stack
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { name: 'React', desc: 'Frontend Framework', color: 'bg-cyan-500/20', textColor: 'text-cyan-400' },
                { name: 'FastAPI', desc: 'Backend API', color: 'bg-green-500/20', textColor: 'text-green-400' },
                { name: 'MongoDB', desc: 'Database', color: 'bg-emerald-500/20', textColor: 'text-emerald-400' },
                { name: 'n8n', desc: 'Workflow Automation', color: 'bg-orange-500/20', textColor: 'text-orange-400' },
                { name: 'Resend', desc: 'Email Service', color: 'bg-purple-500/20', textColor: 'text-purple-400' },
                { name: 'JWT', desc: 'Authentication', color: 'bg-blue-500/20', textColor: 'text-blue-400' }
              ].map((tech, i) => (
                <div 
                  key={i}
                  className="bg-[#111827] border border-white/10 rounded-xl p-5 text-center card-glow"
                >
                  <div className={`w-14 h-14 ${tech.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                    <Code className={tech.textColor} size={24} />
                  </div>
                  <h4 className="text-white font-semibold mb-1">{tech.name}</h4>
                  <p className="text-gray-500 text-xs">{tech.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Timeline */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                How We Delivered
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Delivery Process
              </h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-stretch gap-4">
                {[
                  { icon: <Lightbulb size={20} />, title: 'Discovery & Planning', num: '01' },
                  { icon: <Target size={20} />, title: 'System Design', num: '02' },
                  { icon: <Code size={20} />, title: 'Development', num: '03' },
                  { icon: <Shield size={20} />, title: 'Testing & QA', num: '04' },
                  { icon: <Rocket size={20} />, title: 'Production Deployment', num: '05' }
                ].map((phase, i) => (
                  <div key={i} className="flex-1 relative">
                    <div className="bg-[#1F2937] border border-white/10 rounded-xl p-5 h-full">
                      <div className="text-emerald-400/30 text-4xl font-extrabold mb-3">{phase.num}</div>
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-3">
                        <div className="text-emerald-400">{phase.icon}</div>
                      </div>
                      <h4 className="text-white font-semibold text-sm">{phase.title}</h4>
                    </div>
                    {i < 4 && (
                      <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                        <ChevronRight className="text-emerald-500" size={20} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Outcomes Section */}
        <section className="py-16 sm:py-24">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Results
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Outcomes
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { 
                  icon: <LayoutDashboard size={24} />, 
                  title: 'Centralised Management', 
                  desc: 'All repair requests managed from a single platform.'
                },
                { 
                  icon: <Zap size={24} />, 
                  title: 'Reduced Administration', 
                  desc: 'Automation replaced repetitive manual processes.'
                },
                { 
                  icon: <Users size={24} />, 
                  title: 'Volunteer Coordination', 
                  desc: 'Assignment and tracking improved significantly.'
                },
                { 
                  icon: <MessageSquare size={24} />, 
                  title: 'Customer Communication', 
                  desc: 'Automated notifications keep users informed throughout the process.'
                }
              ].map((outcome, i) => (
                <div 
                  key={i}
                  className="bg-[#111827] border border-white/10 rounded-xl p-6 card-glow"
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                    <div className="text-emerald-400">{outcome.icon}</div>
                  </div>
                  <h4 className="text-white font-semibold mb-2">{outcome.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{outcome.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                What They Say
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Client Feedback
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                { 
                  name: 'Tony Vroon',
                  role: 'CEO', 
                  org: 'Peterborough Repair Café',
                  quote: 'Enthusiastic and full of energy. Adam really understood what we needed and had a complete appointment booking system going for us within a week or two.'
                },
                { 
                  name: 'Andrew Chapman',
                  role: 'Director', 
                  org: 'Peterborough Repair Café',
                  quote: 'Adam built a website and an automated system to manage our bookings for the repair cafe, he was diligent and responsive to all of our questions, and resolved all the queries we raised in a timely manner, very helpful and professional. If automation of business processes is your thing, you are in the right place.'
                }
              ].map((testimonial, i) => (
                <div 
                  key={i}
                  className="bg-[#1F2937] border border-white/10 rounded-2xl p-6 sm:p-8 relative"
                >
                  <Quote className="absolute top-6 right-6 text-emerald-500/20" size={48} />
                  
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  
                  <p className="text-gray-400 italic mb-6 leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <UserCircle className="text-emerald-400" size={28} />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}, {testimonial.org}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px]"></div>
          </div>

          <div className="section-container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white mb-6">
                Need a System Like This?
              </h2>
              <p className="text-gray-400 mb-8 text-base sm:text-lg leading-relaxed">
                Whether you're managing customers, bookings, requests, operations or internal workflows, we build custom systems designed around your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/#contact" className="btn-primary justify-center">
                  Book Consultation
                  <ArrowRight size={18} />
                </Link>
                <Link to="/#services" className="btn-secondary justify-center">
                  View Services
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 sm:py-12 border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/mushonga_logo_circular.png"
                alt="Mushonga Systems"
                className="h-10 w-auto"
              />
              <span className="text-white font-bold text-sm sm:text-base">Mushonga Systems</span>
            </div>
            
            <div className="text-center">
              <p className="text-gray-500 text-xs sm:text-sm">
                © {new Date().getFullYear()} Mushonga Systems. All rights reserved.
              </p>
              <p className="text-gray-600 text-[10px] sm:text-xs mt-1">
                Mushonga Systems is a trading name of ATM29 LTD. Registered in England and Wales. Company No. 13457210.
              </p>
            </div>

            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/#services" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Services</Link>
              <Link to="/" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Home</Link>
              <Link to="/#contact" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      <Lightbox 
        images={galleryImages}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};

// Globe icon (missing from imports)
const Globe = ({ size, className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

export default CaseStudyPage;
